// src/components/CalendarSelector.js

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const LeftArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9.707 4.293a1 1 0 00-1.414 1.414L11.586 8H4a1 1 0 100 2h7.586l-3.293 3.293a1 1 0 001.414 1.414l5-5a1 1 0 000-1.414l-5-5z" clipRule="evenodd" />
  </svg>
);

const RightArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L13.586 11H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);

export default function CalendarSelector({ availabilitySlots = [], onSelect, onBack }) {
  const [dates, setDates] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const arr = [];
    
    // próximos 14 dias
    for (let i = 1; i <= 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dow = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Check if there are any available slots for this day of week
      const daySlots = availabilitySlots.filter(s => s.dayOfWeek === dow);
      const hasAvailableSlots = daySlots.some(s => s.isAvailable);
      
      if (hasAvailableSlots) {
        arr.push(d);
      }
    }
    
    setDates(arr);
  }, [availabilitySlots]);

  const formatDate = (date) => {
    const dayOfWeek = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('pt-BR', { month: 'long' });
    
    return {
      dayOfWeek: dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1),
      fullDate: `${day} de ${month}`
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Escolha uma Data</h2>
        <p className="text-gray-600 mt-1">Selecione o dia que melhor se encaixa na sua agenda</p>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-2xl">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-lg">Nenhuma data disponível nos próximos 14 dias.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {dates.map(d => {
              const { dayOfWeek, fullDate } = formatDate(d);
              return (
                <motion.button
                  key={d.toISOString()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSel(d)}
                  className={`p-4 rounded-xl border transition-all duration-200 ${
                    sel?.getTime() === d.getTime()
                      ? 'border-[#e91e63] bg-[#e91e63]/5 shadow-sm'
                      : 'border-gray-200 hover:border-[#e91e63]/50 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-[#e91e63]">{dayOfWeek}</span>
                    <span className="text-base font-semibold text-gray-900 mt-1">{fullDate}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          <div className="flex justify-between pt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onBack}
              className="rounded-xl py-3 px-6 flex items-center border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <LeftArrow /> Voltar
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => sel && onSelect({ date: sel })}
              disabled={!sel}
              className="bg-[#e91e63] text-white rounded-xl py-3 px-8 flex items-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d81557] transition-colors"
            >
              Próximo <RightArrow />
            </motion.button>
          </div>
        </>
      )}
    </div>
  );
}
